import React, {useEffect, useRef, useState} from 'react';
import {Modal} from "../../../components/shared/Modal/Modal";
import {Controller, useForm} from 'react-hook-form';
import stylesInputs from '../../../assets/styles/inputs.module.css';
import {Spinner} from "react-bootstrap";
import {ALLOWED_FILE_TYPES, MAX_FILE_SIZE} from "../../../Utils/uploadFileSettings";
import {SubUser} from "../../../services/models/sub_user";
import Select from "react-select";
import {useParams} from "react-router-dom";

export interface RecommendationModalProps {
    show: boolean;
    onClose: () => void;
    onSave: (data: FormValues, file: File | null) => void;
    onDelete?: (id: string) => void;
    initialValues?: Partial<FormValues>;
    initialFile?: File | null;
    isEdit?: boolean;
    subUsersSectorData?: Partial<SubUser>[] | undefined
}

export interface FormValues {
    name: string;
    text: string;
    deadline: string;
    goal: string;
    in_charge: string;
    id?: string;
}

const RecommendationModal: React.FC<RecommendationModalProps> = ({
                                                                     show,
                                                                     onClose,
                                                                     onSave,
                                                                     onDelete,
                                                                     initialValues = {},
                                                                     initialFile = null,
                                                                     isEdit = false,
                                                                     subUsersSectorData = []
                                                                 }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(initialFile);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {sectorId} = useParams();
    const {register, handleSubmit, setValue, reset, control, formState: {errors}} = useForm<FormValues>({
        defaultValues: initialValues
    });

    useEffect(() => {
        if (show) {
            reset(initialValues);
            setSelectedFile(initialFile);
            setUploadError(null);
            if (initialValues && initialValues.in_charge) {
                setValue('in_charge', initialValues.in_charge);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const formatFileSize = (bytes: number): string => {
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const validateFile = (file: File): string | null => {
        if (!ALLOWED_FILE_TYPES.FILES.includes(file.type)) {
            return 'Недопустимый тип файла';
        }
        if (file.size > MAX_FILE_SIZE) {
            return 'Размер файла превышает 10 МБ';
        }
        return null;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            setSelectedFile(null);
            setUploadError(null);
            return;
        }
        const error = validateFile(file);
        if (error) {
            setSelectedFile(null);
            setUploadError(error);
        } else {
            setSelectedFile(file);
            setUploadError(null);
        }
    };

    const modalFields = (
        <>
            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                <label className={stylesInputs.label}>Введите название рекомендации</label>
                <div className={stylesInputs.inputWrapper}>
                    <input
                        type="text"
                        className={stylesInputs.mainInput}
                        placeholder={"Название рекомендации"}
                        {...register("name", {required: true})}
                    />
                </div>
                {errors.name && <span className={stylesInputs.error}>Это поле обязательно</span>}
            </div>
            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                <label className={stylesInputs.label}>Введите описание рекомендации</label>
                <div className={stylesInputs.inputWrapper}>
                    <input
                        type="text"
                        className={stylesInputs.mainInput}
                        placeholder={"Описание"}
                        {...register("text", {required: true})}
                    />
                </div>
                {errors.text && <span className={stylesInputs.error}>Это поле обязательно</span>}
            </div>
            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                <label className={stylesInputs.label}>Выберите специалиста</label>
                <div className={stylesInputs.inputWrapper}>
                    <Controller
                        control={control}
                        name="in_charge"
                        rules={{required: true}}
                        render={({field}) => (
                            <Select
                                {...field}
                                classNamePrefix="react-select"
                                placeholder="Выберите специалиста"

                                options={
                                    (subUsersSectorData || [])
                                        .filter(user => typeof user._id === 'string' && typeof user.full_name === 'string')
                                        .map(user => ({
                                            value: user._id as string,
                                            label: user.full_name as string
                                        }))
                                }
                                value={subUsersSectorData?.find(user => user._id === field.value)
                                    ? {
                                        value: field.value,
                                        label: subUsersSectorData.find(user => user._id === field.value)?.full_name || ''
                                    }
                                    : null}
                                onChange={option => field.onChange(option ? option.value : '')}
                                isClearable
                                styles={{
                                    container: (base) => ({
                                        ...base,
                                        width: "100%"
                                    }),
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: '38px',
                                        borderRadius: 16,
                                        padding: "5px 3px",
                                        borderColor: state.isFocused ? '#1a64a5' : state.hasValue ? '#1a64a5' : '#afafaf',
                                        boxShadow: "none",
                                        fontSize: '16px',
                                        backgroundColor: state.hasValue ? '#f7f7f7' : '#fff',
                                        '&:hover': {
                                            borderColor: state.isFocused ? '#1a64a5' : state.hasValue ? '#1a64a5' : '#afafaf',
                                            backgroundColor: state.hasValue ? 'transparent' : '#f7f7f7'
                                        }
                                    }),
                                    menu: base => ({...base, zIndex: 9999}),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: "#fff",
                                        color: '#363636',
                                        fontWeight: state.isSelected ? '600' : '400',
                                    }),
                                    singleValue: base => ({...base, color: '#212529'}),
                                    placeholder: base => ({...base, color: '#adb5bd'}),
                                }}
                            />
                        )}
                    />
                </div>
                {errors.in_charge && <span className={stylesInputs.error}>Это поле обязательно</span>}
            </div>
            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                <label className={stylesInputs.label}>Укажите срок</label>
                <div className={stylesInputs.inputWrapper}>
                    <input
                        type="text"
                        className={stylesInputs.mainInput}
                        placeholder={"Укажите срок"}
                        {...register("deadline", {required: true})}
                    />
                </div>
                {errors.deadline && <span className={stylesInputs.error}>Это поле обязательно</span>}
            </div>
            <div className={`mb-3 ${stylesInputs.formGroup}`}>
                <label className={stylesInputs.label}>Введите целевой показатель</label>
                <div className={stylesInputs.inputWrapper}>
                    <input
                        type="text"
                        className={stylesInputs.mainInput}
                        placeholder={"Целевой показатель"}
                        {...register("goal", {required: true})}
                    />
                </div>
                {errors.goal && <span className={stylesInputs.error}>Это поле обязательно</span>}
            </div>
            <div className={stylesInputs.formGroup}>
                <div
                    className={`${stylesInputs.fileUpload} ${
                        !selectedFile
                            ? ''
                            : uploadError
                                ? stylesInputs.error
                                : stylesInputs.success
                    }`}
                >
                    <input
                        type="file"
                        className={stylesInputs.fileUploadInput}
                        onChange={handleFileChange}
                        ref={fileInputRef}
                    />
                    <div className={`${stylesInputs.fileUploadContent} ${selectedFile ? stylesInputs.withFile : ''}`}>
                        {!selectedFile ? (
                            <span>Загрузить файл</span>
                        ) : (
                            <>
                                <div className={stylesInputs.fileInfo}>
                                    <span>{selectedFile.name}</span>
                                    <span className={stylesInputs.fileSize}>
                                        {formatFileSize(selectedFile.size)}
                                    </span>
                                </div>
                                <div className={stylesInputs.fileActions}>
                                    {isUploading && (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {uploadError && <span className={stylesInputs.error}>{uploadError}</span>}
                {!selectedFile && !uploadError && (
                    <label className={stylesInputs.label}>
                        Размер файла не должен превышать 10 МБ
                    </label>
                )}
            </div>
        </>
    );

    const handleSave = handleSubmit((data) => {
        const payload: any = {
            ...data,
            sector: sectorId,
        };
        if (!payload.status) {
            delete payload.status;
        }
        if (!payload.text) {
            delete payload.text;
        }
        onSave(payload, selectedFile);
        onClose();
    });

    const handleDelete = () => {

        if (onDelete && initialValues && (initialValues as any).id) {
            onDelete((initialValues as any).id);
        }
    };


    return (
        <Modal
            show={show}
            onClose={onClose}
            title={isEdit ? "Редактировать рекомендацию" : "Добавить рекомендацию"}
            showSave={true}
            showDelete={isEdit}
            onSave={handleSave}
            onDelete={isEdit ? handleDelete : undefined}
            disabledSave={false}
        >

            {modalFields}
        </Modal>
    );
};

export default RecommendationModal;
